import { cdnUrl } from '@/shared/config';

export type FeatureSectionId = 'clients' | 'providers' | 'business';
export type FeatureStatus = 'available' | 'in_progress' | 'planned';
export type FeatureMediaType =
  | 'hero_phone_clients'
  | 'hero_phone_providers'
  | 'hero_tablet_providers'
  | 'countdown_queue_phone'
  | 'appointment_reminders_preview'
  | 'no_show_protection_preview'
  | 'book_and_pay_preview'
  | 'individual_booking_links_preview'
  | 'rapid_transfer_preview'
  | 'auto_payout_preview'
  | 'reports_analytics_preview'
  | 'screenshot';

export interface FeatureMedia {
  type: FeatureMediaType;
  imageSrc?: string;
  imageAlt?: string;
  caption?: string;
}

export interface FeatureCatalogItem {
  slug: string;
  name: string;
  section: FeatureSectionId;
  tagline: string;
  shortDescription: string;
  status: FeatureStatus;
  overview: string;
  highlights: string[];
  roadmap: string[];
  media: FeatureMedia;
}

export interface FeatureMenuSection {
  id: FeatureSectionId;
  title: string;
  columns: number;
  features: FeatureCatalogItem[];
}

export const FEATURE_STATUS_META: Record<
  FeatureStatus,
  { label: string; color: 'success' | 'warning' | 'default' }
> = {
  available: { label: 'Available now', color: 'success' },
  in_progress: { label: 'In progress', color: 'warning' },
  planned: { label: 'Planned', color: 'default' },
};

type FeatureCatalogSeedItem = Omit<FeatureCatalogItem, 'media'> & {
  media?: FeatureMedia;
};

const featureCatalogSeed: FeatureCatalogSeedItem[] = [
  {
    slug: 'online-booking',
    name: 'Online Booking',
    section: 'clients',
    tagline: 'Let clients book 24/7 from any device.',
    shortDescription: 'Real-time availability, service selection, and confirmation.',
    status: 'available',
    overview:
      'Online Booking powers Donvio discovery, service selection, and scheduling so clients can book without phone calls.',
    highlights: [
      'Real-time appointment slots tied to provider schedules.',
      'Service, staff, and shop selection in one flow.',
      'Booking confirmation and immediate dashboard visibility.',
    ],
    roadmap: [
      'Improve recurring appointment workflows.',
      'Add stronger repeat-booking shortcuts for returning clients.',
      'Expand booking funnel analytics for conversion optimization.',
    ],
    media: {
      type: 'screenshot',
      imageSrc: `${cdnUrl}/static/features/online-booking/preview-v1.webp`,
      imageAlt: 'Online booking flow preview',
    },
  },
  {
    slug: 'payments',
    name: 'Payments',
    section: 'clients',
    tagline: 'Pay securely at booking with card or Apple Pay.',
    shortDescription: 'Fast, secure checkout with clear totals and instant confirmation.',
    status: 'available',
    overview:
      'Payments in Donvio are powered by Stripe so clients can check out confidently during booking. Card checkout is always available, Apple Pay appears on supported devices and browsers, and clients can add a tip before confirming their appointment.',
    highlights: [
      'Secure Stripe checkout directly inside the booking flow.',
      'Apple Pay support on compatible devices and browsers.',
      'Transparent totals, including service price, fees, and optional tip.',
      'Instant booking confirmation after successful payment.',
    ],
    roadmap: [
      'Broaden supported payment methods over time.',
      'Add richer payment reconciliation exports.',
      'Enhance failed-payment recovery and retry experience.',
    ],
    media: {
      type: 'screenshot',
      imageSrc: `${cdnUrl}/static/features/payments/preview-v1.webp`,
      imageAlt: 'Payments checkout preview',
    },
  },
  {
    slug: 'smart-reminders',
    name: 'Smart Reminders',
    section: 'clients',
    tagline: 'Keep clients on schedule with proactive reminders.',
    shortDescription: 'Automated reminders and queue-aware timing updates.',
    status: 'available',
    overview:
      'Appointment reminders combine scheduled outreach and queue-awareness so clients get timing signals before service begins.',
    highlights: [
      'Automated reminder templates for appointment workflows.',
      'Queue and next-up reminder support to reduce no-shows.',
      'Consistent notification timing across booking states.',
    ],
    roadmap: [
      'Add more personalization controls by shop.',
      'Introduce A/B testing for reminder timing.',
      'Expand opt-in preference management for clients.',
    ],
    media: {
      type: 'appointment_reminders_preview',
    },
  },
  {
    slug: 'live-queue',
    name: 'Live Queue & Countdown',
    section: 'clients',
    tagline: 'Watch your place in line and know exactly when it\'s your turn.',
    shortDescription: 'Real-time queue position and live countdown timer for clients.',
    status: 'available',
    overview:
      'Donvio\'s live queue is powered by providers starting each appointment with a countdown timer on their end. As each service begins, every client in the queue sees their position update in real time. When you\'re next up, a live countdown shows exactly how long until your turn — no guessing, no wasted time in the lobby.',
    highlights: [
      'Providers start each appointment as a countdown timer, powering the live queue.',
      'Clients see their real-time queue position update as appointments progress.',
      'Live countdown timer when you\'re next — know exactly when to be ready.',
    ],
    roadmap: [
      'Add push notifications for queue position changes.',
      'Expand estimated wait time accuracy with service-duration awareness.',
      'Introduce queue-based walk-in support for shops.',
    ],
    media: {
      type: 'countdown_queue_phone',
      caption: 'Same client phone mockup used on landing to represent live queue countdown.',
    },
  },
  {
    slug: 'no-show-prevention',
    name: 'No-Show Prevention',
    section: 'providers',
    tagline: 'Reduce no-shows with smarter policies and reminders.',
    shortDescription: 'Queue timing, reminders, and policy settings.',
    status: 'available',
    overview:
      'No-Show Prevention combines policy controls, live queue status, and reminder signals to keep appointment attendance high.',
    highlights: [
      'Cancellation policy support with enforcement logic.',
      'Queue-aware timing that tells clients when to head out.',
      'Reminder workflows designed to reduce missed slots.',
    ],
    roadmap: [
      'Add deeper analytics for policy impact by provider.',
      'Improve recommended default policy configurations.',
      'Expand automated follow-up for late or missed bookings.',
    ],
    media: {
      type: 'no_show_protection_preview',
    },
  },
  {
    slug: 'pay-at-booking',
    name: 'Pay at Booking',
    section: 'providers',
    tagline: 'Keep your service price intact while clients pay an all-in total.',
    shortDescription: 'Booking fee is added on top of service price in one confirmed checkout flow.',
    status: 'available',
    overview:
      'Pay at Booking streamlines scheduling and payment into one connected journey without reducing provider service pricing. Donvio does not eat into the listed service price: if a provider sets a service to $35, the client can see an all-in menu total like $37.64 where $2.64 is the booking fee added on top.',
    highlights: [
      'Donvio does not deduct from provider service prices.',
      'Booking fee is bundled into the client-facing menu total.',
      'Clients see transparent all-in pricing before confirming payment.',
      'Appointments and payment confirmation happen in one flow.',
    ],
    roadmap: [
      'Reduce checkout friction with stronger saved-payment UX.',
      'Improve cancellation and refund handling flows.',
      'Add richer payment event tracking for support operations.',
    ],
    media: {
      type: 'book_and_pay_preview',
    },
  },
  {
    slug: 'scheduling',
    name: 'Scheduling',
    section: 'providers',
    tagline: 'Manage availability, breaks, time off, and service windows from one place.',
    shortDescription: 'Daily operations controls for providers and teams.',
    status: 'available',
    overview:
      'Scheduling keeps calendars accurate by combining provider availability, service duration, time-off management, and operating constraints.',
    highlights: [
      'Provider and shop schedule configuration support.',
      'Service duration-aware time slot generation.',
      'Availability controls that prevent double-booking.',
      'Request and manage time off — vacations, emergencies, and days off.',
    ],
    roadmap: [
      'Add stronger recurring scheduling templates.',
      'Expand advanced calendar exception controls.',
      'Improve schedule conflict diagnostics for teams.',
    ],
    media: {
      type: 'screenshot',
      imageSrc: `${cdnUrl}/static/features/scheduling/preview-v1.webp`,
      imageAlt: 'Scheduling management preview',
    },
  },
  {
    slug: 'workflow',
    name: 'Workflow',
    section: 'providers',
    tagline: 'Start appointments, track your countdown, and stay in control of your day.',
    shortDescription: 'Appointment start, countdown timer, and workstation experience.',
    status: 'available',
    overview:
      'Workflow is the daily operating rhythm for providers on Donvio. Each appointment has a Start button — the heart of the queue and countdown system. When you tap Start, a live countdown timer begins based on the service duration, keeping you on pace while giving every client in the queue real-time feedback on their wait. For the best experience, we recommend working with a tablet at your workstation so you can start appointments, monitor the countdown, and manage your queue at a glance.',
    highlights: [
      'Each appointment has a Start button that activates the live countdown timer.',
      'The countdown timer keeps you on pace and powers the live queue for clients.',
      'Clients see real-time wait updates as each appointment progresses.',
      'For the best experience, use a tablet at your workstation.',
      'Monitor your queue, current appointment, and upcoming clients from one screen.',
    ],
    roadmap: [
      'Add quick-action controls for common mid-appointment events.',
      'Expand workstation layout for multi-chair visibility.',
      'Introduce service-phase tracking within active appointments.',
    ],
    media: {
      type: 'hero_tablet_providers',
      caption: 'Provider tablet mockup from landing hero showing active workflow countdown.',
    },
  },
  {
    slug: 'client-management',
    name: 'Client Management',
    section: 'providers',
    tagline: 'Track client history and build stronger retention habits.',
    shortDescription: 'Client records, appointment history, and relationship context.',
    status: 'available',
    overview:
      'Client Management gives providers context on booking behavior and relationship history for better service continuity.',
    highlights: [
      'Client lists and profile views in provider dashboards.',
      'Appointment history visibility for repeat clients.',
      'Operational filtering for faster client lookup.',
    ],
    roadmap: [
      'Add richer client lifecycle and retention insights.',
      'Improve note-taking and profile enrichment options.',
      'Expand segmentation for marketing and follow-up workflows.',
    ],
    media: {
      type: 'screenshot',
      imageSrc: `${cdnUrl}/static/features/client-management/preview-v1.webp`,
      imageAlt: 'Client management preview',
    },
  },
  {
    slug: 'personal-booking-links',
    name: 'Personal Booking Links',
    section: 'providers',
    tagline: 'Give every provider a direct booking URL clients can tap instantly.',
    shortDescription: 'Direct provider links for social, text, and in-shop sharing.',
    status: 'available',
    overview:
      'Personal booking links remove friction from provider marketing. Instead of sending clients through a full browsing flow, each provider can share one direct URL that lands clients on their personal booking page.',
    highlights: [
      'Each provider has a public, shareable booking URL.',
      'Clients open the provider page directly and book in fewer taps.',
      'Links are available from discovery cards, shop provider cards, and setup actions.',
    ],
    roadmap: [
      'Add link performance tracking and attribution.',
      'Support campaign-level link customization.',
      'Expand deep-link handling for mobile distribution.',
    ],
    media: {
      type: 'individual_booking_links_preview',
    },
  },
  {
    slug: 'instant-payout',
    name: 'Instant Payout',
    section: 'providers',
    tagline: 'Finish a service and trigger payout flow to your connected Stripe account.',
    shortDescription: 'Fast payout workflow tied to completed appointments.',
    status: 'available',
    overview:
      'Instant Payout turns completed appointments into immediate payout movement. Once a service is completed, Donvio starts the transfer process so providers can track net earnings without waiting on manual payout handling.',
    highlights: [
      'Payout sequence starts when appointments are completed.',
      'Net earnings route directly to connected Stripe accounts.',
      'Provider dashboards show transfer status and payout visibility.',
    ],
    roadmap: [
      'Add clearer exception handling for delayed payouts.',
      'Improve transfer event notifications.',
      'Expand payout analytics for owners and staff.',
    ],
    media: {
      type: 'rapid_transfer_preview',
    },
  },
  {
    slug: 'rent-collect',
    name: 'Rent Collect',
    section: 'business',
    tagline: 'Automate booth rental collection with tiered pricing that scales with revenue.',
    shortDescription: 'Multi-tier booth rental pricing with automated invoicing via Stripe.',
    status: 'available',
    overview:
      'Rent Collect gives shop owners a structured way to manage booth rental fees. Define tiered pricing based on revenue thresholds — flat-rate or percentage — and let Donvio handle the invoicing. Whether you run a single-chair rental or a full shop of independent providers, booth rental tiers keep pricing transparent and collection predictable.',
    highlights: [
      'Multi-tier pricing with revenue threshold escalation.',
      'Flat-rate or percentage fee options per tier.',
      'Supports both commission and booth rental contract types.',
      'Automated invoicing powered by Stripe.',
    ],
    roadmap: [
      'Add rental payment history and receipt exports.',
      'Improve tier recommendations based on revenue trends.',
      'Expand automated reminders for upcoming rental payments.',
    ],
    media: {
      type: 'screenshot',
      imageSrc: `${cdnUrl}/static/features/rent-collect/preview-v1.webp`,
      imageAlt: 'Rent collect booth rental management preview',
    },
  },
  {
    slug: 'owner-dashboard',
    name: 'Owner Dashboard',
    section: 'business',
    tagline: 'Monitor bookings, schedules, and business health from one place.',
    shortDescription: 'Unified operational dashboard with commission and booth rental support.',
    status: 'available',
    overview:
      'The Owner Dashboard is the central hub for monitoring bookings, schedules, and operational health across your shop. Whether you operate on a commission model or booth rental, the dashboard adapts to your business structure so you can track earnings, payouts, and team activity in one place.',
    highlights: [
      'Dashboard surfaces daily business activity at a glance.',
      'Supports both commission-based and booth rental business models.',
      'Role-based views support owners and staff.',
      'Quick access to appointments, services, and client workflows.',
    ],
    roadmap: [
      'Add cross-location views for larger operators.',
      'Improve alerting and exception surfaces in dashboard widgets.',
      'Expand workflow shortcuts for high-frequency actions.',
    ],
    media: {
      type: 'screenshot',
      imageSrc: `${cdnUrl}/static/features/owner-dashboard/preview-v1.webp`,
      imageAlt: 'Owner dashboard preview',
    },
  },
  {
    slug: 'staff-management',
    name: 'Staff Management',
    section: 'business',
    tagline: 'Organize your team with role-aware workflows.',
    shortDescription: 'Staff onboarding, permissions, and team operations.',
    status: 'available',
    overview:
      'Staff Management supports team operations with role permissions, onboarding flows, and service-level access controls.',
    highlights: [
      'Staff invitation and onboarding support.',
      'Role and permission controls for operational safety.',
      'Team-level access to schedules, services, and clients.',
    ],
    roadmap: [
      'Add advanced role templates for larger teams.',
      'Improve staff performance and utilization visibility.',
      'Expand owner tools for staffing recommendations.',
    ],
    media: {
      type: 'screenshot',
      imageSrc: `${cdnUrl}/static/features/staff-management/preview-v1.webp`,
      imageAlt: 'Staff management preview',
    },
  },
  {
    slug: 'services',
    name: 'Services',
    section: 'business',
    tagline: 'Build your service menu with professional images and smart presets.',
    shortDescription: 'Service catalog with 60+ presets, rich image selection, and flexible pricing.',
    status: 'available',
    overview:
      'Services in Donvio let providers build a polished, professional service menu in minutes. Choose from over 60 service presets — fades, tapers, beard trims, lineups, and more — each with a curated library of high-quality images to represent your work. Browse images by category, style, or hair type, or search by name. Set up quickly with the Quick Add tab, or build services manually with full control over pricing, duration, and descriptions.',
    highlights: [
      'Over 60 service presets with professional images ready to use.',
      'Image selector with search, category, style, and hair type filtering.',
      'Quick Add tab for rapid service setup alongside manual entry.',
      'Service cards adapt across shop view, booking selection, and management.',
    ],
    roadmap: [
      'Expand preset coverage to additional provider types beyond the current catalog.',
      'Add service bundling and package creation.',
      'Improve image upload support for custom service photos.',
    ],
    media: {
      type: 'screenshot',
      imageSrc: `${cdnUrl}/static/features/services/preview-v1.webp`,
      imageAlt: 'Services catalog preview',
    },
  },
  {
    slug: 'automated-payouts',
    name: 'Automated Payouts',
    section: 'business',
    tagline: 'Automate provider payouts and reduce manual transfer work.',
    shortDescription: 'Connected-account payout cycles with clear status visibility.',
    status: 'available',
    overview:
      'Automated Payouts leverage Stripe-connected payout flows so owners and providers can move from completed services to transferred funds without manual payout handling.',
    highlights: [
      'Automated payout cycles tied to completed appointment revenue.',
      'Connected account support for predictable transfer operations.',
      'Dashboard visibility into batch status and pending payouts.',
    ],
    roadmap: [
      'Improve payout exception diagnostics and recovery.',
      'Add clearer owner-level payout summaries by team.',
      'Expand payout timeline notifications.',
    ],
    media: {
      type: 'auto_payout_preview',
    },
  },
  {
    slug: 'revenue-tracking',
    name: 'Revenue Tracking',
    section: 'business',
    tagline: 'See exactly what your shop earns across every chair and contract type.',
    shortDescription: 'Staff performance breakdowns, booth rental collection, and owner revenue visibility.',
    status: 'available',
    overview:
      'Revenue Tracking gives shop owners a clear picture of how money flows through their business. View staff performance side by side — revenue, tips, appointments, and owner share — broken down by contract type. Track booth rental payments with collection status, invoicing, and payment history so nothing slips through the cracks.',
    highlights: [
      'Staff performance table with revenue, tips, and appointment counts.',
      'Owner share visibility across commission and booth rental contracts.',
      'Booth rental payment tracking with collected, pending, and overdue status.',
      'Payment history with filtering by period, status, type, and staff member.',
    ],
    roadmap: [
      'Add revenue trend charts and period-over-period comparisons.',
      'Improve exportability for accounting and tax reporting.',
      'Expand multi-location revenue rollups.',
    ],
    media: {
      type: 'screenshot',
      imageSrc: `${cdnUrl}/static/features/revenue-tracking/preview-v1.webp`,
      imageAlt: 'Revenue tracking staff performance preview',
    },
  },
];

const defaultScreenshotPath = (featureSlug: string): string =>
  `/assets/features/${featureSlug}/preview.png`;

export const featureCatalog: FeatureCatalogItem[] = featureCatalogSeed.map((feature) => ({
  ...feature,
  media: feature.media ?? {
    type: 'screenshot',
    imageAlt: `${feature.name} screenshot`,
    caption: `Add a screenshot at ${defaultScreenshotPath(feature.slug)}`,
  },
}));

export const featureMenuSections: FeatureMenuSection[] = [
  {
    id: 'clients',
    title: 'For Your Clients',
    columns: 1,
    features: featureCatalog.filter(feature => feature.section === 'clients'),
  },
  {
    id: 'providers',
    title: 'For Providers',
    columns: 1,
    features: featureCatalog.filter(feature => feature.section === 'providers'),
  },
  {
    id: 'business',
    title: 'For Business',
    columns: 1,
    features: featureCatalog.filter(feature => feature.section === 'business'),
  },
];

export const getFeatureBySlug = (featureSlug: string): FeatureCatalogItem | undefined =>
  featureCatalog.find(feature => feature.slug === featureSlug);

export const getFeaturePath = (featureSlug: string): string =>
  `/features/${featureSlug}`;
